import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import petTypesService from '../../services/petTypesService';

const initialState = {
  petTypes: [],
  isLoading: false,
  error: null,
  success: false,
};

export const fetchPetTypes = createAsyncThunk(
  'petTypes/fetchAll',
  async (_, thunkAPI) => {
    try {
      return await petTypesService.getAll();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch pet types';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createPetType = createAsyncThunk(
  'petTypes/create',
  async (formData, thunkAPI) => {
    try {
      return await petTypesService.create(formData);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create pet type';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updatePetType = createAsyncThunk(
  'petTypes/update',
  async ({ id, formData }, thunkAPI) => {
    try {
      return await petTypesService.update(id, formData);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update pet type';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deletePetType = createAsyncThunk(
  'petTypes/delete',
  async (id, thunkAPI) => {
    try {
      await petTypesService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete pet type';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const petTypesSlice = createSlice({
  name: 'petTypes',
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPetTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPetTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petTypes = action.payload;
      })
      .addCase(fetchPetTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createPetType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPetType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petTypes.push(action.payload);
        state.success = true;
      })
      .addCase(createPetType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updatePetType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePetType.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.petTypes.findIndex((pt) => pt._id === action.payload._id);
        if (index !== -1) {
          state.petTypes[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updatePetType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deletePetType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePetType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.petTypes = state.petTypes.filter((pt) => pt._id !== action.payload);
        state.success = true;
      })
      .addCase(deletePetType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = petTypesSlice.actions;
export default petTypesSlice.reducer;
